import { useContext } from 'react';
import { Card, Progress, Spin, Tooltip } from 'antd';
import { HeartOutlined, InfoCircleOutlined, ShopOutlined, TeamOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { IDashboardSocialInstitutionsData } from '../../../../services/http/mappers/DashboardMapper';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import { formatCnpj } from '../../../../utils/Masks';
import { poeValorComPontosEmMil } from '../../../../utils/Conversores';

interface SocialInstitutionsStatsProps {
    data: IDashboardSocialInstitutionsData;
    totalUsers: number;
    isLoading: boolean;
    hasSocialInstitutionModule?: boolean;
}

export default function SocialInstitutionsStats({
    data,
    totalUsers,
    isLoading,
    hasSocialInstitutionModule = true,
}: SocialInstitutionsStatsProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const institutions = data?.institutions ?? [];
    const totalLinkedUsers = data?.totalLinkedUsers ?? 0;
    const totalEntities = data?.total ?? 0;
    const totalUnlinkedUsers = Math.max(0, (totalUsers || 0) - totalLinkedUsers);

    return (
        <Card
            title={
                <div style={{ paddingTop: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 20 }}>Instituições Sociais Vinculadas</div>
                    <div style={{ fontWeight: 400, fontSize: 18, color: '#888' }}>Engajamento comunitário e apoio dos munícipes</div>
                </div>
            }
            style={{
                background: isDark ? '#1f1f1f' : '#FFFFFF',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            bodyStyle={{ padding: '8px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}
        >
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Spin />
                </div>
            ) : !hasSocialInstitutionModule ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 300,
                        textAlign: 'center',
                        color: '#888',
                        gap: 12,
                        padding: 24,
                    }}
                >
                    <InfoCircleOutlined style={{ fontSize: 36, color: '#faad14' }} />
                    <div style={{ fontSize: 18, fontWeight: 600, color: isDark ? '#e8e8e8' : '#333' }}>
                        Módulo de Instituição Social Não Habilitado
                    </div>
                    <div style={{ fontSize: 15, maxWidth: 380, color: '#888' }}>
                        O município selecionado não possui o módulo de Instituições Sociais ativado nas configurações do sistema.
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                padding: '10px 14px',
                                borderRadius: 10,
                                background: isDark ? '#141414' : '#fafafa',
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                borderLeft: '4px solid #1677ff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1677ff', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <TeamOutlined style={{ fontSize: 13 }} />
                                <span>Total de Usuários</span>
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#1677ff', marginTop: 4, lineHeight: 1.2 }}>
                                {poeValorComPontosEmMil(totalUsers || 0)}
                            </div>
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Cadastrados</div>
                        </div>

                        <div
                            style={{
                                padding: '10px 14px',
                                borderRadius: 10,
                                background: isDark ? '#141414' : '#fafafa',
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                borderLeft: '4px solid #eb2f96',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#eb2f96', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <HeartOutlined style={{ fontSize: 13 }} />
                                <span>Apoiadores</span>
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#eb2f96', marginTop: 4, lineHeight: 1.2 }}>
                                {poeValorComPontosEmMil(totalLinkedUsers)}
                            </div>
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Com vínculo</div>
                        </div>

                        <div
                            style={{
                                padding: '10px 14px',
                                borderRadius: 10,
                                background: isDark ? '#141414' : '#fafafa',
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                borderLeft: '4px solid #fa8c16',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#fa8c16', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <UserDeleteOutlined style={{ fontSize: 13 }} />
                                <span>Sem Vínculo</span>
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#fa8c16', marginTop: 4, lineHeight: 1.2 }}>
                                {poeValorComPontosEmMil(totalUnlinkedUsers)}
                            </div>
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Sem escolha</div>
                        </div>

                        <div
                            style={{
                                padding: '10px 14px',
                                borderRadius: 10,
                                background: isDark ? '#141414' : '#fafafa',
                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                borderLeft: '4px solid #52c41a',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#52c41a', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <ShopOutlined style={{ fontSize: 13 }} />
                                <span>Entidades Cadastradas</span>
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#52c41a', marginTop: 4, lineHeight: 1.2 }}>
                                {poeValorComPontosEmMil(totalEntities)}
                            </div>
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>No sistema</div>
                        </div>
                    </div>

                    <div style={{ flex: 1, minHeight: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: isDark ? '#aaa' : '#666', marginBottom: 10 }}>
                            Distribuição de Apoio por Entidade
                        </div>

                        {institutions.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#888', padding: '30px 0', fontSize: 14 }}>
                                Nenhuma instituição social encontrada
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    maxHeight: 480,
                                    overflowY: 'auto',
                                    paddingRight: 4,
                                }}
                            >
                                {institutions.map((inst, index) => {
                                    const percent = totalLinkedUsers > 0
                                        ? Math.min(100, Math.round((inst.totalUsers / totalLinkedUsers) * 100))
                                        : 0;

                                    return (
                                        <div
                                            key={inst.id || index}
                                            style={{
                                                padding: '12px 16px',
                                                borderRadius: 10,
                                                background: isDark ? '#141414' : '#fafafa',
                                                border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                                borderLeft: '4px solid #eb2f96',
                                                transition: 'all 0.18s ease',
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <Tooltip title={inst.name}>
                                                        <div
                                                            style={{
                                                                fontWeight: 600,
                                                                fontSize: 16,
                                                                color: isDark ? '#e8e8e8' : '#222',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {inst.name}
                                                        </div>
                                                    </Tooltip>
                                                    <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                                                        {formatCnpj(inst.cnpj)}
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ fontWeight: 700, fontSize: 16, color: '#eb2f96' }}>
                                                            {poeValorComPontosEmMil(inst.totalUsers)}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: '#888' }}>
                                                            {percent}% do total
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: 8 }}>
                                                <Progress
                                                    percent={percent}
                                                    size="small"
                                                    strokeColor="#eb2f96"
                                                    trailColor={isDark ? '#262626' : '#f0f0f0'}
                                                    showInfo={false}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
}
